import React, { useCallback, useEffect, useState } from "react";
import { EMAIL_TRIGGER_ATTACHMENT_SOURCES } from "./emailTriggerAttachments";

function attachmentSummary(attachmentSources, attachmentLabels) {
  const labels = (Array.isArray(attachmentLabels) ? attachmentLabels : []).filter(Boolean);
  if (labels.length > 0) return labels.join(", ");
  const ids = Array.isArray(attachmentSources) ? attachmentSources : [];
  if (ids.length === 0) return "";
  const byId = new Map(EMAIL_TRIGGER_ATTACHMENT_SOURCES.map((s) => [s.id, s.label]));
  return ids.map((id) => byId.get(id) || id).join(", ");
}

export default function PhaseCompleteEmailModal({
  title,
  loading,
  loadError,
  preview,
  sending,
  sendError,
  onCancel,
  onSend,
  onCompleteWithoutEmail,
}) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    if (!preview) return;
    setTo(String(preview.to || ""));
    setSubject(String(preview.subject || ""));
    setBody(String(preview.body || ""));
  }, [preview]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !sending) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, sending]);

  const handleSend = useCallback(() => {
    onSend({ to: to.trim(), subject, body });
  }, [onSend, to, subject, body]);

  const attachmentLine = attachmentSummary(preview?.attachmentSources, preview?.attachmentLabels);
  const canSend = Boolean(to.trim() && (subject.trim() || body.trim()));
  const showSkip = typeof onCompleteWithoutEmail === "function" && !loading;

  return (
    <div
      className="email-triggers-modal-backdrop"
      role="presentation"
      onClick={sending ? undefined : onCancel}
    >
      <div
        className="email-triggers-modal phase-complete-email-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="phase-complete-email-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="phase-complete-email-modal-title" className="email-triggers-modal-title">
          {title}
        </h3>
        <p className="email-triggers-modal-sub">
          Review the notification email below. Edit recipients, subject, or message as needed, then send to complete
          this phase — or complete without sending if needed.
        </p>

        {loading ? (
          <p className="project-detail-muted">Loading email preview…</p>
        ) : loadError ? (
          <p className="project-detail-error" role="alert">
            {loadError}
          </p>
        ) : (
          <>
            {Array.isArray(preview?.warnings) && preview.warnings.includes("no_recipients") ? (
              <p className="project-detail-muted phase-complete-email-modal-note" role="status">
                No recipients are configured for this trigger. Add at least one email address below, or complete
                without sending.
              </p>
            ) : null}
            {Array.isArray(preview?.warnings) && preview.warnings.includes("empty_template") ? (
              <p className="project-detail-muted phase-complete-email-modal-note" role="status">
                The email template is empty in Settings → Emails. Add a subject or body below before sending.
              </p>
            ) : null}
            <label className="email-triggers-field">
              <span className="email-triggers-field-label">To</span>
              <input
                type="text"
                className="email-triggers-input email-triggers-input-wide"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="email@example.com, other@example.com"
                disabled={sending}
                autoComplete="off"
              />
            </label>
            <label className="email-triggers-field">
              <span className="email-triggers-field-label">Subject</span>
              <input
                type="text"
                className="email-triggers-input email-triggers-input-wide"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={sending}
              />
            </label>
            <label className="email-triggers-field">
              <span className="email-triggers-field-label">Message</span>
              <textarea
                className="email-triggers-textarea"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={10}
                disabled={sending}
              />
            </label>
            {attachmentLine ? (
              <p className="project-detail-muted phase-complete-email-modal-note">
                Attachments: {attachmentLine}
              </p>
            ) : null}
            {sendError ? (
              <p className="project-detail-error" role="alert">
                {sendError}
              </p>
            ) : null}
          </>
        )}

        <div className="email-triggers-modal-actions">
          {!loading && !loadError ? (
            <button
              type="button"
              className="email-triggers-button email-triggers-button-primary"
              onClick={handleSend}
              disabled={sending || !canSend}
            >
              {sending ? "Sending…" : "Send & complete phase"}
            </button>
          ) : null}
          {showSkip ? (
            <button
              type="button"
              className={`email-triggers-button${loadError || !canSend ? " email-triggers-button-primary" : ""}`}
              onClick={onCompleteWithoutEmail}
              disabled={sending}
            >
              {sending ? "Saving…" : "Complete without email"}
            </button>
          ) : null}
          <button
            type="button"
            className="email-triggers-button email-triggers-button-ghost"
            onClick={onCancel}
            disabled={sending}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
